import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useFetchSingle } from '../hooks/useFetchSingle'
import SubscriptionService from '../services/SubscriptionService'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import {
    Payment,
    Plan,
    Subscription,
    SubscriptionData,
    User,
} from '../types/Subscription'
import axios, { AxiosError, AxiosResponse } from 'axios'
import { usePluginContext } from './PluginContext'
import { ApiRoutes } from '../services/ApiService'
import {NotificationData} from "../types/NotificationData";
import {useFetchData} from "../hooks/useFetchData";

type AdminContextType = {
    subscription: SubscriptionData | null
    getSubscription: () => void
    changeUser: (data: User | null) => void
    service: SubscriptionService
    user: User | null
    getUser: () => void
    loggingIn: boolean
    stripe: Promise<Stripe | null>
    paymentMethods: Payment[]
    plans: Plan[]
    getPaymentMethods: () => void
    getPlans: () => void
    activeSub: Subscription | null
    chooseSub: (s: Subscription) => void
    subscriptions: Subscription[]
    getSubscriptions: () => void
    notifications: NotificationData[] | null,
    getNotifications: () => void
}

const AdminContext = createContext<AdminContextType | undefined>(undefined)

type AdminContextProps = {
    children: React.ReactNode | React.ReactNode[]
}

export const AdminContextProvider = ({
    children,
}: AdminContextProps): JSX.Element => {
    const stripePromise = useMemo(() => {
        return loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY ?? '')
    }, [])
    const [loggingIn, setLoggingIn] = useState<boolean>(
        !!localStorage.getItem('zr_customizer_token')
    )
    const [userData, setUserData] = useState<User | null>(null)
    const [paymentMethods, setPaymentMethods] = useState<Payment[]>([])
    const [plans, setPlans] = useState<Plan[]>([])
    const [activeSub, setActiveSub] = useState<Subscription | null>(null)
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
    const { plugin } = usePluginContext()

    const subscriptionService = useMemo(() => {
        const service = new SubscriptionService()
        service.addInterceptors(undefined, (error: AxiosError | Error) => {
            if (axios.isAxiosError(error)) {
                const { status } = (error.response as AxiosResponse) ?? {}

                if (status === 401) {
                    service.setToken(null)
                    setUserData(null)
                }
            }

            return Promise.reject(error)
        })
        return new SubscriptionService()
    }, [])

    const changeUser = useCallback((data: User | null) => {
        setUserData(data)
    }, [])

    const chooseSub = useCallback((data: Subscription | null) => {
        setActiveSub(data)
    }, [])

    const { data: subscription, fetch: getSubscription } =
        useFetchSingle<SubscriptionData>(ApiRoutes.UPDATE_SUBSCRIPTION)

    const getPaymentMethods = useCallback(() => {
        subscriptionService.getPaymentMethods().then((res) => {
            if (res.data?.data) setPaymentMethods(res.data.data)
        })
    }, [subscriptionService])

    const getPlans = useCallback(() => {
        if (plugin)
            subscriptionService.getPlans(plugin.plugin).then((res) => {
                if (res.data?.data) setPlans(res.data.data)
            })
    }, [plugin, subscriptionService])

    const getUserData = useCallback(() => {
        subscriptionService
            .getUser()
            .then((res) => {
                if (res.data?.data) setUserData(res.data.data)
                setLoggingIn(false)
            })
            .catch(() => {
                setUserData(null)
            })
            .finally(() => {
                setLoggingIn(false)
            })
    }, [subscriptionService])

    const getSubscriptions = useCallback(() => {
        if (plugin)
            subscriptionService.getSubscriptions(plugin.plugin).then((res) => {
                setSubscriptions(res.data)
            })
    }, [subscriptionService, plugin])

    const {
        data: notifications,
        fetch: getNotifications,
    } = useFetchData<NotificationData>(ApiRoutes.NOTIFICATION)

    useEffect(() => {
        getNotifications()
    }, []);

    useEffect(() => {
        getSubscription()
    }, [getSubscription])

    useEffect(() => {
        if (subscriptionService.hasToken()) getUserData()
    }, [getUserData, subscriptionService])

    useEffect(() => {
        if (userData) getPaymentMethods()
    }, [getPaymentMethods, userData])

    useEffect(() => {
        if (userData) getSubscriptions()
    }, [getSubscriptions, userData])

    return (
        <AdminContext.Provider
            value={{
                stripe: stripePromise,
                subscription,
                getSubscription,
                changeUser,
                service: subscriptionService,
                user: userData,
                getUser: getUserData,
                loggingIn,
                paymentMethods,
                getPaymentMethods,
                plans,
                getPlans,
                activeSub,
                subscriptions,
                getSubscriptions,
                chooseSub,
                notifications,
                getNotifications
            }}
        >
            {children}
        </AdminContext.Provider>
    )
}

export const useAdmin = () => {
    const data = useContext(AdminContext)

    if (data === undefined) {
        throw new Error('useAdmin must used within AdminContextProvider')
    }

    return data
}
